import React, { useRef } from 'react';
import { Download, Printer } from 'lucide-react';
import html2pdf from 'html2pdf.js';
import { useLanguage } from '../context/LanguageContext';

const TaxDiscountResolutionForm = () => {
  const { language } = useLanguage();
  const componentRef = useRef();

  const handlePrint = () => {
    window.print();
  };

  const handleDownloadPdf = () => {
    const element = componentRef.current;
    const opt = {
      margin: 0,
      filename: language === 'marathi' ? '50Percent_Tax_Discount_Resolution.pdf' : '50Percent_Tax_Discount_Resolution_EN.pdf',
      image: { type: 'jpeg', quality: 1 },
      html2canvas: { scale: 3, useCORS: true, logging: false },
      jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' }
    };
    html2pdf().from(element).set(opt).save();
  };

  return (
    <div className="min-h-screen bg-gray-200 py-8 font-sans print:py-0 print:bg-white flex flex-col items-center">
      <style>
        {`
          .page {
            width: 210mm;
            height: 297mm;
            padding: 20mm;
            margin: 0 auto 20px auto;
            box-sizing: border-box;
            position: relative;
            background-color: white;
            overflow: hidden;
            box-shadow: 0 0 15px rgba(0,0,0,0.1);
          }
          
          .dashed-line {
            border-bottom: 1px dashed black;
            display: inline-block;
          }

          @media print {
            @page {
              size: A4 portrait;
              margin: 0mm !important;
            }
            html, body {
              margin: 0 !important;
              padding: 0 !important;
              width: 210mm !important;
              height: 297mm !important;
              background-color: white !important;
              -webkit-print-color-adjust: exact !important;
              print-color-adjust: exact !important;
            }
            .page {
              margin: 0 !important;
              box-shadow: none !important;
              width: 210mm !important;
              height: 297mm !important;
              page-break-after: always !important;
              break-after: page !important;
              break-inside: avoid !important;
              border: none !important;
            }
            .no-print {
              display: none !important;
            }
            .min-h-screen {
              min-height: auto !important;
              padding: 0 !important;
            }
          }
        `}
      </style>
      
      {/* Controls Container */}
      <div className="w-full max-w-[210mm] mb-6 flex justify-end space-x-4 print:hidden px-4 no-print">
        <button
          onClick={handlePrint}
          className="flex items-center px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 shadow-sm transition-colors"
        >
          <Printer className="w-4 h-4 mr-2" />
          {language === 'marathi' ? 'Print' : 'Print'}
        </button>
        <button
          onClick={handleDownloadPdf}
          className="flex items-center px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 shadow-sm transition-colors"
        >
          <Download className="w-4 h-4 mr-2" />
          {language === 'marathi' ? 'Download PDF' : 'Download PDF'}
        </button>
      </div>

      <div ref={componentRef} className="flex flex-col items-center text-black">
        {/* PAGE 1 */}
        <div className="bg-white relative box-border flex flex-col page">
           <div className="flex justify-center items-center text-[15px] font-bold mb-2">
              <span>{language === 'marathi' ? 'कार्यालय ग्रामपंचायत' : 'Gram Panchayat Office'}</span>
              <span className="dashed-line w-40 h-4 mx-1"></span>
              <span>{language === 'marathi' ? 'ता' : 'Tal'}</span>
              <span className="dashed-line w-32 h-4 mx-1"></span>
              <span>{language === 'marathi' ? 'जि.' : 'Dist.'}</span>
              <span className="dashed-line w-32 h-4 mx-1"></span>
           </div>

           <div className="text-center text-[15px] mb-4">
              <span className="font-bold">{language === 'marathi' ? 'दिनांक:- &nbsp; &nbsp; &nbsp; &nbsp;/ &nbsp; &nbsp; &nbsp; &nbsp;/२० &nbsp; &nbsp; रोजी झालेल्या ग्रामसभा बैठकीचे इतिवृत्त नोंदवही' : 'Date:- &nbsp; &nbsp; &nbsp; &nbsp;/ &nbsp; &nbsp; &nbsp; &nbsp;/20 &nbsp; &nbsp; Minutes Register of Gramsabha Meeting Held On'}</span>
           </div>

           <table className="w-full border-collapse border border-black text-[14px]">
              <thead>
                 <tr>
                    <th className="border border-black p-1 w-12 text-center font-normal">{language === 'marathi' ? 'विषय नंबर' : 'Subject No.'}</th>
                    <th className="border border-black p-1 w-12 text-center font-normal">{language === 'marathi' ? 'ठराव नंबर' : 'Resolution No.'}</th>
                    <th className="border border-black p-2 text-center text-[15px] font-normal">{language === 'marathi' ? 'सभेपुढे विचाराकरीता आलेले विषय व ठराव' : 'Subjects and Resolutions brought before the meeting for consideration'}</th>
                    <th className="border border-black p-1 w-32 text-center font-normal leading-tight">{language === 'marathi' ? 'शेरे आणि तारखेसह ठरावाची अंमल- बजावणी' : 'Implementation of resolution with remarks and date'}</th>
                 </tr>
              </thead>
              <tbody className="h-[30px]">
                 <tr>
                    <td className="border border-black"></td>
                    <td className="border border-black"></td>
                    <td className="border border-black"></td>
                    <td className="border border-black"></td>
                 </tr>
              </tbody>
           </table>

           <div className="border border-black border-t-0 p-4 flex-grow flex flex-col relative text-[15px] leading-snug space-y-2">
              <p className="flex items-end flex-wrap indent-8">
                 <span>{language === 'marathi' ? 'आज दिनांक : &nbsp; &nbsp; &nbsp;/ &nbsp; &nbsp; &nbsp; /२० &nbsp; &nbsp; &nbsp; &nbsp; वार रोजी ग्रामपंचायत कार्यालय' : 'Today Date : &nbsp; &nbsp; &nbsp;/ &nbsp; &nbsp; &nbsp; /20 &nbsp; &nbsp; &nbsp; &nbsp; Day, at Gram Panchayat Office'}</span>
                 <span className="dashed-line flex-grow h-4 mx-1"></span>
                 <span>{language === 'marathi' ? 'ची सर्वसाधारण/' : "'s General/"}</span>
                 <span className="w-full mt-1 flex items-end">
                    <span>{language === 'marathi' ? 'विशेष ग्रामसभा सरपंच श्री/सौ.' : 'Special Gramsabha Sarpanch Mr./Mrs.'}</span>
                    <span className="dashed-line flex-grow h-4 mx-1"></span>
                    <span>{language === 'marathi' ? 'यांच्या अध्यक्षतेखाली' : 'under the chairmanship of'}</span>
                 </span>
                 <span className="w-full mt-1 flex items-end">
                    <span>{language === 'marathi' ? 'ग्रामपंचायत कार्यालयात सकाळी/दुपारी ठीक :' : 'Gram Panchayat Office exactly at Morning/Afternoon :'}</span>
                    <span className="dashed-line w-16 h-4 mx-2"></span>
                    <span>{language === 'marathi' ? 'वाजता बोलविण्यात आली.' : 'was convened.'}</span>
                 </span>
              </p>
              
              <p className="indent-8 mt-1">{language === 'marathi' ? 'या सभेत खालील ठराव मांडण्यात आला.' : 'The following resolution was moved in this meeting.'}</p>

              <div className="flex mt-1">
                 <span className="font-bold text-[16px] mr-2">{language === 'marathi' ? 'विषय क्र.' : 'Subject No.'}</span>
                 <span className="font-bold text-[16px] ml-2 text-justify">{language === 'marathi' ? ': मुख्यमंत्री समृद्ध पंचायत राज अभियान अंतर्गत ग्रामपंचायतींकडून निवासी मालमत्ता कर व अन्य कर थकबाकी वसुलीत सवलत देणेबाबत- निर्णय घेणे' : ': Taking decision regarding giving concession in recovery of arrears of residential property tax and other taxes by Gram Panchayats under Chief Minister Samruddha Panchayat Raj Abhiyan'}</span>
              </div>

              <p className="text-justify indent-12 mt-2">
                 {language === 'marathi' ? 'सूचक यांनी सुचवले की "मुख्यमंत्री समृद्ध पंचायत राज अभियान" अंतर्गत ग्रामपंचायतींच्या थकीत कर वसुलीला गती देण्यासाठी, थकबाकीच्या एक रकमी वसुलीकरिता आणि थकबाकीची वसुल केलेली रक्कम विकास प्रक्रियेत सामावून घेण्यासाठी ग्रामपंचायतींकडून निवासी मालमत्ता कर व अन्य करांच्या थकबाकी वसुलीत सवलत देण्याबाबत शासननिर्णय दिनांक १३/११/२०२५ नुसार शासनाने निर्णय घेतलेला आहे.' : 'The proposer suggested that to accelerate the recovery of outstanding taxes of Gram Panchayats under the "Chief Minister Samruddha Panchayat Raj Abhiyan", for one-time recovery of arrears and to include the recovered amount of arrears in the development process, the government has taken a decision as per the Government Resolution dated 13/11/2025 regarding giving concession in the recovery of arrears of residential property tax and other taxes by Gram Panchayats.'}
              </p>

              <p className="text-justify indent-12 mt-2">
                 {language === 'marathi' ? 'सदरील शासन निर्णयानुसार निवासी मालमत्ताधारकांना दिनांक ०१ एप्रिल, २०२५ पूर्वीच्या एकूण निवासी मालमत्ता कर, पाणी पट्टी आणि दिवाबत्ती कर इत्यादी करांच्या थकबाकीमध्ये ५०% टक्के सवलत द्यायची किंवा नाही, याचा निर्णय घेण्याचा पूर्ण अधिकार ग्रामपंचायतींना देण्यात आलेला असून यासाठी ग्रामपंचायतीने विशेष ग्रामसभा बोलावून त्यात बहुमताने ठराव मंजूर करणे अनिवार्य आहे. त्यानुसार आजच्या ग्रामसभेत याबाबत निर्णय घ्यावयाचा आहे. त्यास अनुमोदक यांनी अनुमोदन दिले.' : 'As per the said government resolution, full authority has been given to the Gram Panchayats to decide whether or not to give 50% concession in the arrears of total residential property tax, water tax and street light tax etc. prior to 01 April 2025 to residential property holders, and for this it is mandatory for the Gram Panchayat to convene a special Gramsabha and pass a resolution by majority in it. Accordingly, a decision is to be taken in today\'s Gramsabha. The seconder seconded it.'}
              </p>

              <p className="text-justify indent-12 mt-2">
                 {language === 'marathi' ? (
                   <>त्यानंतर अध्यक्ष यांच्या निर्देशानुसार सचिवांनी ग्रामसभेत ग्रामविकास <span className="text-blue-700">शासन निर्णय क्रमांक: व्हीपीएम-२०२५/प्र.क्र.६२(computer no.१३९२६८९) / पंरा-१/ दिनांक १३ नोव्हेंबर २०२५</span> चे सविस्तर वाचन केले.</>
                 ) : (
                   <>After that, as per the directions of the President, the Secretary read in detail the Rural Development <span className="text-blue-700">Government Resolution No.: VPM-2025/C.R.62 (computer no.1392689) / Panra-1/ dated 13 November 2025</span> in the Gramsabha.</>
                 )}
              </p>

              <p className="text-justify indent-12 mt-2">
                 {language === 'marathi' ? 'सभेत चर्चा करण्यात येऊन ग्रामसभेत पुढीलप्रमाणे निर्णय घेण्यात आला:' : 'After discussion in the meeting, the following decision was taken in the Gramsabha:'}
              </p>

              <div className="mt-4">
                 <table className="w-full border-collapse border border-black text-[14px]">
                    <thead>
                       <tr>
                          <th className="border border-black p-2 text-center w-12 font-bold">{language === 'marathi' ? 'अ.क्र.' : 'Sr.No.'}</th>
                          <th className="border border-black p-2 text-center font-bold">{language === 'marathi' ? 'कराचे नाव' : 'Name of Tax'}</th>
                          <th className="border border-black p-2 text-center font-bold w-48 leading-tight">{language === 'marathi' ? (
                            <>१ एप्रिल, २०२५ पूर्वीच्या थकबाकीमध्ये ५०% टक्के सवलत देण्यात यावी का?<br/>होय/नाही</>
                          ) : (
                            <>Should 50% concession be given in arrears prior to 1st April 2025?<br/>Yes/No</>
                          )}</th>
                          <th className="border border-black p-2 text-center font-bold w-40 leading-tight">{language === 'marathi' ? 'सोबत सन २०२५-२६ या चालू वर्षाची पूर्ण रक्कम भरणा करणे आवश्यक' : 'Payment of full amount of the current year 2025-26 is required along with this'}</th>
                       </tr>
                    </thead>
                    <tbody>
                       {[
                          [language === 'marathi' ? '१.' : '1.', language === 'marathi' ? 'घरपट्टी (मालमत्ता कर)' : 'House Tax (Property Tax)', language === 'marathi' ? 'होय' : 'Yes', language === 'marathi' ? 'होय' : 'Yes'],
                          [language === 'marathi' ? '२.' : '2.', language === 'marathi' ? 'दिवाबत्ती कर' : 'Street Light Tax', language === 'marathi' ? 'होय' : 'Yes', language === 'marathi' ? 'होय' : 'Yes'],
                          [language === 'marathi' ? '३.' : '3.', language === 'marathi' ? 'आरोग्य कर' : 'Health Tax', language === 'marathi' ? 'होय' : 'Yes', language === 'marathi' ? 'होय' : 'Yes'],
                          [language === 'marathi' ? '४.' : '4.', language === 'marathi' ? 'सामान्य पाणीपट्टी' : 'General Water Tax', '', language === 'marathi' ? 'होय' : 'Yes'],
                          [language === 'marathi' ? '५.' : '5.', language === 'marathi' ? 'खास पाणीपट्टी' : 'Special Water Tax', '', language === 'marathi' ? 'होय' : 'Yes'],
                          [language === 'marathi' ? '६.' : '6.', '', '', '']
                       ].map((row, i) => (
                          <tr key={i}>
                             <td className="border border-black p-1 pl-2">{row[0]}</td>
                             <td className="border border-black p-1 pl-2">{row[1]}</td>
                             <td className="border border-black p-1 pl-2">{row[2]}</td>
                             <td className="border border-black p-1 pl-2">{row[3]}</td>
                          </tr>
                       ))}
                    </tbody>
                 </table>
              </div>
           </div>
        </div>

        {/* PAGE 2 */}
        <div className="bg-white relative box-border flex flex-col page">
           <div className="border border-black border-t-0 p-8 flex-grow flex flex-col relative text-[15px] leading-relaxed">
              <div className="space-y-2 mt-4">
                 <div className="flex">
                    <span className="mr-2">{language === 'marathi' ? '१.' : '1.'}</span>
                    <p className="text-justify">{language === 'marathi' ? 'सदर सवलत केवळ निवासी मालमत्ताधारकांसाठी (Residential Property Holders) असून औद्योगिक, वाणिज्यिक व इतर मालमत्तांसाठी नाही.' : 'The said concession is only for Residential Property Holders and not for industrial, commercial and other properties.'}</p>
                 </div>
                 <div className="flex">
                    <span className="mr-2">{language === 'marathi' ? '२.' : '2.'}</span>
                    <p className="text-justify">{language === 'marathi' ? 'थकबाकीवर देण्यात येणारी ५०% सवलत ही ग्रामसभेने मान्यता दिलेल्या कर- प्रकारांसाठी लागू राहील. तसेच ज्या कराच्या थकबाकीवर ग्रामसभेने ५०% सवलत दिलेली नाही; त्या कर-प्रकाराची संपूर्ण रक्कमही सोबत भरावी लागेल.' : 'The 50% concession given on arrears will be applicable to the tax types approved by the Gramsabha. Also, the tax type on whose arrears the Gramsabha has not given 50% concession; the entire amount of that tax type will also have to be paid along with it.'}</p>
                 </div>
                 <div className="flex">
                    <span className="mr-2">{language === 'marathi' ? '३.' : '3.'}</span>
                    <p className="text-justify">{language === 'marathi' ? 'थकबाकीवर देण्यात येणारी ५०% सवलत ही एक-रकमी कर भरणा केल्यासच लागू राहील.' : 'The 50% concession given on arrears will be applicable only if a one-time tax payment is made.'}</p>
                 </div>
                 <div className="flex">
                    <span className="mr-2">{language === 'marathi' ? '४.' : '4.'}</span>
                    <p className="text-justify">{language === 'marathi' ? 'सदर सवलत मुख्यमंत्री समृद्ध पंचायत राज अभियान कालावधीत लागू राहील.' : 'The said concession will be applicable during the period of Chief Minister Samruddha Panchayat Raj Abhiyan.'}</p>
                 </div>
              </div>

              <p className="text-justify indent-12 mt-8">
                 {language === 'marathi' ? 'ग्रामसभेत बहुमताने/ सर्वानुमते वरीलप्रमाणे निर्णय घेण्यात आला. त्यास मान्यता देण्यात आली. व ठराव बहुमताने /सर्वानुमते संमत करण्यात आला.' : 'The above decision was taken by majority/unanimously in the Gramsabha. It was approved. And the resolution was passed by majority/unanimously.'}
              </p>

              <div className="flex mt-12 justify-between w-full">
                 <div className="flex w-1/2">
                    <span className="mr-1">{language === 'marathi' ? 'सुचक:' : 'Proposer:'}</span>
                    <span className="dashed-line flex-grow h-4"></span>
                 </div>
                 <div className="flex w-1/2 ml-4">
                    <span className="mr-1">{language === 'marathi' ? 'अनुमोदन:' : 'Seconder:'}</span>
                    <span className="dashed-line flex-grow h-4"></span>
                 </div>
              </div>

              <div className="flex justify-end mt-32">
                 <div className="flex flex-col items-center w-[400px]">
                    <div className="flex justify-between w-full px-8 mb-2">
                       <p className="font-bold">{language === 'marathi' ? 'सरपंच' : 'Sarpanch'}</p>
                       <p className="font-bold">{language === 'marathi' ? 'ग्रामपंचायत अधिकारी' : 'Gram Panchayat Officer'}</p>
                    </div>
                    <div className="flex items-end w-full">
                       <span className="font-bold mr-2">{language === 'marathi' ? 'ग्रामपंचायत' : 'Gram Panchayat'}</span>
                       <span className="dashed-line flex-grow h-4"></span>
                    </div>
                 </div>
              </div>
           </div>
        </div>
      </div>
    </div>
  );
};

export default TaxDiscountResolutionForm;
